/**
 * MyAccountPage — /me/account.
 *
 * Self-serve "change password" form for any authenticated user whose
 * account has a local credential (Entra-only users get a 409 from the
 * BE which surfaces as a calm field-level error). Gated by the
 * `passwordReset` feature flag at the router level.
 *
 * Three required fields: current, new, confirm. The BE owns the policy
 * (length, no-shared-default, must-differ); we mirror the length floor
 * client-side for instant feedback but never trust it — the BE response
 * is the source of truth.
 */

import { useId, useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '../api/client'
import { changeOwnPassword } from '../api/auth-password'
import { useSession } from '../auth/auth-context'
import { PageHead } from '../components/primitives/PageHead'
import { Button } from '../components/primitives/Button'

const MIN_NEW_PASSWORD_LENGTH = 8

function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return fallback
}

export function MyAccountPage(): JSX.Element {
  const queryClient = useQueryClient()
  const { user } = useSession()
  const currentId = useId()
  const newId = useId()
  const confirmId = useId()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All three fields are required.')
      return
    }
    if (newPassword.length < MIN_NEW_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_NEW_PASSWORD_LENGTH} characters.`)
      return
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.')
      return
    }
    if (newPassword === currentPassword) {
      setError('New password must differ from the current password.')
      return
    }

    setSubmitting(true)
    try {
      await changeOwnPassword({ currentPassword, newPassword })
      setSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      // The session shape carries `mustChangePassword`; the BE just cleared
      // it. Refetch so the AppShell interstitial unblocks immediately.
      await queryClient.invalidateQueries({ queryKey: ['session', 'me'] })
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to change password.'))
    } finally {
      setSubmitting(false)
    }
  }

  // SSO (Microsoft Entra) users have no local password — the nav item is hidden
  // for them, but guard direct navigation here too. Only an explicit `false`
  // gates it, so older sessions / test fixtures still render the form.
  if (user?.hasLocalPassword === false) {
    return (
      <>
        <PageHead eyebrow="Personal" title="Account" subtitle="Manage your sign-in credentials." />
        <div
          style={{
            maxWidth: 480,
            background: 'var(--paper-raised)',
            border: '1px solid var(--line)',
            borderRadius: 8,
            padding: 24,
            boxShadow: 'var(--shadow-card)',
            fontSize: 13,
            color: 'var(--ink-2)',
            lineHeight: 1.5,
          }}
        >
          You sign in with Microsoft, so there's no password to manage here.
          Change your password through your Microsoft account.
        </div>
      </>
    )
  }

  return (
    <>
      <PageHead
        eyebrow="Personal"
        title="Account"
        subtitle="Manage your sign-in credentials."
      />

      <div
        style={{
          maxWidth: 480,
          background: 'var(--paper-raised)',
          border: '1px solid var(--line)',
          borderRadius: 8,
          padding: 24,
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 18,
            fontWeight: 400,
            margin: '0 0 8px',
            color: 'var(--ink)',
          }}
        >
          Change password
        </h2>
        <p
          style={{
            fontSize: 13,
            color: 'var(--ink-2)',
            margin: '0 0 20px',
            lineHeight: 1.5,
          }}
        >
          Local password sign-in only. Microsoft Entra users manage their
          password via Microsoft.
        </p>

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <PasswordField
            id={currentId}
            label="Current password"
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
          />
          <PasswordField
            id={newId}
            label="New password"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            hint={`At least ${MIN_NEW_PASSWORD_LENGTH} characters.`}
          />
          <PasswordField
            id={confirmId}
            label="Confirm new password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
          />

          {error ? (
            <div
              role="alert"
              style={{
                marginTop: 12,
                padding: '10px 12px',
                borderRadius: 6,
                background: 'var(--danger-soft)',
                color: 'var(--danger)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}

          {success ? (
            <div
              role="status"
              style={{
                marginTop: 12,
                padding: '10px 12px',
                borderRadius: 6,
                background: 'var(--success-soft)',
                color: 'var(--success)',
                fontSize: 13,
              }}
            >
              Password updated.
            </div>
          ) : null}

          <div style={{ marginTop: 20 }}>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Update password'}
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}

interface PasswordFieldProps {
  id: string
  label: string
  value: string
  onChange: (next: string) => void
  autoComplete: string
  hint?: string
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  hint,
}: Readonly<PasswordFieldProps>): JSX.Element {
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--ink-3)',
          marginBottom: 4,
        }}
      >
        {label}
      </label>
      <input
        id={id}
        type="password"
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          minHeight: 44,
          padding: '8px 12px',
          fontSize: 14,
          fontFamily: 'var(--font-mono)',
          color: 'var(--ink)',
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 6,
          outline: 'none',
        }}
      />
      {hint ? (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: 'var(--ink-3)',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  )
}
